/**
 * Text Normalization — Phase 5 / Plan 05-04 Task 1 (WIDGET-04).
 *
 * 욕설·스팸 필터 파이프라인의 전처리 단계. 이메일 정규화 (`email.ts`) 와 달리
 * 여기서는 **메시지·닉네임 본문** 의 필터 비교 키를 생성한다. 자소 분리 공격
 * (`ㅅㅣㅂㅅㅐㄲㅣ` → `시발새끼`) 을 사전 매칭 가능한 형태로 복원하는 것이 핵심.
 *
 * 정규화 단계 (순서 중요):
 *   1. NFC (Unicode 합성 정규화) — 한글 자모 분해·결합, Roman numeral 등 decomposable
 *      문자 정규화.
 *   2. Zero-width chars 제거 (`\u200B` ZWS, `\u200C` ZWNJ, `\u200D` ZWJ, `\uFEFF` BOM).
 *   3. 전각 → 반각 변환 (U+FF01..U+FF5E → ASCII). `Ａ` → `a`, `１` → `1`.
 *   4. 자소 복원 (`restoreJamo`) — 조합 가능한 초성(U+1100~)/중성(U+1161~)/종성(U+11A7~)
 *      시퀀스를 Hangul Syllable (U+AC00~) 로 재조립. 일반 자모 `ㅅ`(U+3145) ·
 *      `ㅣ`(U+3163) 도 Jamo 블록으로 매핑 후 결합.
 *   5. 공백 합축 (multiple whitespace → single space) + trim.
 *   6. toLowerCase.
 *
 * 정규화 라이브러리:
 *   - Node 22 built-in `String.prototype.normalize('NFC')` — 외부 의존성 없음.
 *   - 자소 복원은 자체 구현 — 공격자가 가시성 상 동일한 욕설을 자소 분리 형태로
 *     입력해도 사전에 매칭되도록.
 *
 * 출력 불변:
 *   - 이모지·특수문자는 보존 (필터 판정은 이들에 대해서는 영향 없음).
 *   - 자소 결합 불가능 잔여 자모는 원문 유지 — 다음 layer (Clean Bot 등) 가 처리.
 *
 * 소비처 (Plan 05-04):
 *   - `apps/api/src/modules/moderation/text-filter.service.ts` 의 filter() 가 입력
 *     text/nickname 을 본 함수로 정규화 후 global/streamer 사전 Trie 매칭.
 */
/** Zero-width / formatting chars. */
const ZERO_WIDTH_REGEX = /[\u200B-\u200D\uFEFF]/g;
/** 연속 공백 합축. */
const MULTI_WS_REGEX = /\s+/g;
/** Fullwidth 범위: U+FF01 ~ U+FF5E. 대응되는 ASCII 는 `c - 0xFEE0`. */
const FULLWIDTH_MIN = 0xff01;
const FULLWIDTH_MAX = 0xff5e;
const FULLWIDTH_OFFSET = 0xfee0;
/** Hangul jamo compat 블록 (ㄱ~ㅎ ㅏ~ㅣ). Range: U+3131 ~ U+318E. */
const COMPAT_CHOSEONG_SET = new Set();
const COMPAT_JUNGSEONG_SET = new Set();
const COMPAT_JONGSEONG_SET = new Set();
// Hangul Syllable composition 상수.
const HANGUL_SBASE = 0xac00; // '가'
const HANGUL_LBASE = 0x1100; // 초성 시작 (Jamo)
const HANGUL_VBASE = 0x1161; // 중성 시작 (Jamo)
const HANGUL_TBASE = 0x11a7; // 종성 시작 - 1 (Jamo, T=0 은 종성 없음)
const HANGUL_VCOUNT = 21;
const HANGUL_TCOUNT = 28;
const HANGUL_NCOUNT = HANGUL_VCOUNT * HANGUL_TCOUNT; // 588
// 참고: HANGUL_LCOUNT(19) · HANGUL_SCOUNT(11172) 는 본 구현 범위에서 직접 사용하지 않음
// (Syllable 합성 공식에 HANGUL_NCOUNT 만 필요).
/**
 * Compat jamo (U+3131~U+318E) → Jamo (U+1100~U+11FF) 매핑.
 *
 * Compat 영역은 "표시용" 자모이고, Jamo 영역이 "조합용" 자모. Syllable composition
 * 은 Jamo 기준으로 이루어지므로 compat → jamo 변환 필수.
 *
 * 완전한 테이블은 복잡하므로 본 함수에 필요한 핵심 자모만 매핑.
 * 한국어 욕설 자소분리 공격 패턴은 대부분 아래 초성 19 + 중성 21 + 기본 종성에서 발생.
 */
const COMPAT_TO_CHOSEONG = {
    0x3131: 0x1100, // ㄱ
    0x3132: 0x1101, // ㄲ
    0x3134: 0x1102, // ㄴ
    0x3137: 0x1103, // ㄷ
    0x3138: 0x1104, // ㄸ
    0x3139: 0x1105, // ㄹ
    0x3141: 0x1106, // ㅁ
    0x3142: 0x1107, // ㅂ
    0x3143: 0x1108, // ㅃ
    0x3145: 0x1109, // ㅅ
    0x3146: 0x110a, // ㅆ
    0x3147: 0x110b, // ㅇ
    0x3148: 0x110c, // ㅈ
    0x3149: 0x110d, // ㅉ
    0x314a: 0x110e, // ㅊ
    0x314b: 0x110f, // ㅋ
    0x314c: 0x1110, // ㅌ
    0x314d: 0x1111, // ㅍ
    0x314e: 0x1112, // ㅎ
};
const COMPAT_TO_JUNGSEONG = {
    0x314f: 0x1161, // ㅏ
    0x3150: 0x1162, // ㅐ
    0x3151: 0x1163, // ㅑ
    0x3152: 0x1164, // ㅒ
    0x3153: 0x1165, // ㅓ
    0x3154: 0x1166, // ㅔ
    0x3155: 0x1167, // ㅕ
    0x3156: 0x1168, // ㅖ
    0x3157: 0x1169, // ㅗ
    0x3158: 0x116a, // ㅘ
    0x3159: 0x116b, // ㅙ
    0x315a: 0x116c, // ㅚ
    0x315b: 0x116d, // ㅛ
    0x315c: 0x116e, // ㅜ
    0x315d: 0x116f, // ㅝ
    0x315e: 0x1170, // ㅞ
    0x315f: 0x1171, // ㅟ
    0x3160: 0x1172, // ㅠ
    0x3161: 0x1173, // ㅡ
    0x3162: 0x1174, // ㅢ
    0x3163: 0x1175, // ㅣ
};
/**
 * Compat jamo → Jongseong(종성) 매핑.
 *
 * 주의: 한국어에서 동일 compat jamo 가 초성으로도 종성으로도 나타날 수 있음
 * (예: `ㄱ` → 초성 0x1100, 종성 0x11A8). 본 함수는 "위치 의존" 매핑 —
 * 먼저 중성이 있는지 보고 그 뒤에 오면 종성 후보로 판정.
 *
 * 종성 영역: U+11A8 ~ U+11C2. T=0 (종성 없음) 은 제외.
 */
const COMPAT_TO_JONGSEONG = {
    0x3131: 0x11a8, // ㄱ
    0x3132: 0x11a9, // ㄲ
    0x3133: 0x11aa, // ㄳ
    0x3134: 0x11ab, // ㄴ
    0x3135: 0x11ac, // ㄵ
    0x3136: 0x11ad, // ㄶ
    0x3137: 0x11ae, // ㄷ
    0x3139: 0x11af, // ㄹ
    0x313a: 0x11b0, // ㄺ
    0x313b: 0x11b1, // ㄻ
    0x313c: 0x11b2, // ㄼ
    0x313d: 0x11b3, // ㄽ
    0x313e: 0x11b4, // ㄾ
    0x313f: 0x11b5, // ㄿ
    0x3140: 0x11b6, // ㅀ
    0x3141: 0x11b7, // ㅁ
    0x3142: 0x11b8, // ㅂ
    0x3144: 0x11b9, // ㅄ
    0x3145: 0x11ba, // ㅅ
    0x3146: 0x11bb, // ㅆ
    0x3147: 0x11bc, // ㅇ
    0x3148: 0x11bd, // ㅈ
    0x314a: 0x11be, // ㅊ
    0x314b: 0x11bf, // ㅋ
    0x314c: 0x11c0, // ㅌ
    0x314d: 0x11c1, // ㅍ
    0x314e: 0x11c2, // ㅎ
};
// Populate sets for fast membership checks (used by restoreJamo classifier).
for (const k of Object.keys(COMPAT_TO_CHOSEONG))
    COMPAT_CHOSEONG_SET.add(Number(k));
for (const k of Object.keys(COMPAT_TO_JUNGSEONG))
    COMPAT_JUNGSEONG_SET.add(Number(k));
for (const k of Object.keys(COMPAT_TO_JONGSEONG))
    COMPAT_JONGSEONG_SET.add(Number(k));
/** 전각 ASCII → 반각. 한글 · 이모지는 영향 없음. */
function fullwidthToHalfwidth(input) {
    let out = '';
    for (const ch of input) {
        const cp = ch.codePointAt(0);
        if (cp >= FULLWIDTH_MIN && cp <= FULLWIDTH_MAX) {
            out += String.fromCodePoint(cp - FULLWIDTH_OFFSET);
        }
        else if (cp === 0x3000) {
            // Ideographic space → 반각 space.
            out += ' ';
        }
        else {
            out += ch;
        }
    }
    return out;
}
/**
 * 자소 분리 공격 복원.
 *
 * 입력 예 (compat jamo 분리): `ㅅㅣㅂㅅㅐㄲㅣ` → `시발새끼`.
 *
 * 알고리즘:
 *   1. 앞에서부터 스캔하며 compat 자모 영역 코드포인트 만날 때마다 (L, V, T?) 트리플
 *      구성 시도.
 *   2. L (초성) 발견 → 다음 문자 V (중성) 체크.
 *      - 있으면 L + V 확정 → 그 다음 문자가 종성 가능한 compat jamo 이면서 뒤따르는
 *        문자가 **초성으로 쓰이는 자음** 이 아닐 때 (즉 다음 음절 시작이 아닐 때)
 *        종성으로 포함. 그렇지 않으면 T=0.
 *      - 종성 선택 규칙: 다음 음절의 L (초성) 후보가 있으면 그것이 다음 음절의 초성.
 *        욕설 자소 패턴은 `ㅅㅣㅂㅅㅐㄲㅣ` 처럼 L-V-L-V-L-V 시퀀스가 전형 → 종성 배정
 *        생략 (단순 보수적 규칙).
 *   3. L + V (+ T) → Hangul Syllable `0xAC00 + (L-0x1100)*588 + (V-0x1161)*28 + T?`.
 *   4. 매칭 실패한 자모는 원문 유지.
 *
 * 보수적 정책: 본 복원은 "명백한 L-V 혹은 L-V-T 쌍" 만 결합. 애매한 경우 원문 유지 →
 * false-positive 를 최소화 (일반 사용자가 단일 자음 `ㅋㅋㅋ` 등을 입력해도 영향 없음).
 */
export function restoreJamo(input) {
    const codepoints = [];
    for (const ch of input) {
        const cp = ch.codePointAt(0);
        codepoints.push(cp);
    }
    const out = [];
    let i = 0;
    while (i < codepoints.length) {
        const cp = codepoints[i];
        // L 후보 (초성 역할 가능 compat jamo) 인가?
        const lJamo = COMPAT_TO_CHOSEONG[cp];
        if (lJamo !== undefined && i + 1 < codepoints.length) {
            const next = codepoints[i + 1];
            const vJamo = COMPAT_TO_JUNGSEONG[next];
            if (vJamo !== undefined) {
                // L+V 확정. T 후보 검사:
                //   i+2 가 compat 자음이면서, **다음 음절 시작으로 해석되지 않을 때만** 종성.
                //   다음 음절 시작 = i+2 가 L(초성) 후보 AND i+3 가 V(중성).
                //   즉 "i+2 가 종성 후보" AND "i+2 가 다음 음절 L-V 쌍의 시작이 아닐 때" → 종성 확정.
                let tJamo = 0;
                let consumed = 2;
                if (i + 2 < codepoints.length) {
                    const jongCandidate = codepoints[i + 2];
                    const tJ = COMPAT_TO_JONGSEONG[jongCandidate];
                    if (tJ !== undefined) {
                        // 다음 음절 시작 여부: i+2 가 L 후보 + i+3 가 V 후보.
                        const isNextSyllableStart = COMPAT_TO_CHOSEONG[jongCandidate] !== undefined &&
                            i + 3 < codepoints.length &&
                            COMPAT_TO_JUNGSEONG[codepoints[i + 3]] !== undefined;
                        if (!isNextSyllableStart) {
                            tJamo = tJ - HANGUL_TBASE;
                            consumed = 3;
                        }
                    }
                }
                // Syllable 합성.
                const lIndex = lJamo - HANGUL_LBASE;
                const vIndex = vJamo - HANGUL_VBASE;
                const syllable = HANGUL_SBASE + (lIndex * HANGUL_NCOUNT) + (vIndex * HANGUL_TCOUNT) + tJamo;
                out.push(syllable);
                i += consumed;
                continue;
            }
        }
        // 결합 불가 — 원문 유지.
        out.push(cp);
        i += 1;
    }
    return String.fromCodePoint(...out);
}
/**
 * 필터 비교 키로 정규화.
 *
 * @param raw 사용자 입력 text.
 * @returns NFC + zero-width 제거 + 전각 반각 + 자소 복원 + 공백 합축 + 소문자.
 *   공 입력 · non-string → 빈 문자열.
 */
export function normalizeForFilter(raw) {
    if (typeof raw !== 'string')
        return '';
    // 1. NFC.
    let s = raw.normalize('NFC');
    // 2. Zero-width 제거.
    s = s.replace(ZERO_WIDTH_REGEX, '');
    // 3. 전각 → 반각.
    s = fullwidthToHalfwidth(s);
    // 4. 자소 복원.
    s = restoreJamo(s);
    // 5. 공백 합축 + trim.
    s = s.replace(MULTI_WS_REGEX, ' ').trim();
    // 6. 소문자.
    s = s.toLowerCase();
    return s;
}
//# sourceMappingURL=text-normalize.js.map